/** FILE FORMATS */

interface Format {
  label: string;
  name: string;
  mimes: string[];
}

//
// Audios
//
// @note We also support audio/webm (recorder), but it is not uploadable
export const AUDIO_FORMATS: Format[] = [
  {
    label: 'WAV',
    name: 'wav',
    mimes: ['audio/wav', 'audio/x-wav'],
  },
];

//
// Documents
//

export const EXCEL_MIMES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const PDF_MIMES = ['application/pdf'];

export const PDF_FORMATS: Format[] = [
  {
    label: 'PDF',
    name: 'pdf',
    mimes: PDF_MIMES,
  },
];

export const WORD_FORMATS: Format[] = [
  {
    label: 'Word',
    name: 'doc, docx',
    mimes: [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
  },
];

export const EXCEL_FORMATS: Format[] = [
  {
    label: 'Excel',
    name: 'xls, xlsx',
    mimes: EXCEL_MIMES,
  },
];

export const POWER_POINT_FORMATS: Format[] = [
  {
    label: 'PowerPoint',
    name: 'ppt, pptx',
    mimes: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
  },
];

export const DOCUMENT_FORMATS: Format[] = [
  ...PDF_FORMATS,
  ...POWER_POINT_FORMATS,
  ...WORD_FORMATS,
  ...EXCEL_FORMATS,
];

//
// Executables
//
export const EXECUTABLE_EXTENSIONS = [
  'a',
  'apk',
  'asp',
  'aspx',
  'bat',
  'bin',
  'bpk',
  'cer',
  'cgi',
  'cmd',
  'com',
  'csh',
  'deploy',
  'dist',
  'distz',
  'dll',
  'dmg',
  'dms',
  'dump',
  'elc',
  'exe',
  'ipa',
  'js',
  'jse',
  'jsp',
  'lha',
  'lrf',
  'lzh',
  'msi',
  'o',
  'obj',
  'php',
  'pkg',
  'pl',
  'portpkg',
  'ps1',
  'ps2',
  'psd1',
  'psm1',
  'py',
  'rpm',
  'sh.',
  'sh',
  'so.',
  'so',
  'vb',
  'vbe',
  'vbs',
  'ws',
  'wsf',
  'wsh',
];

const buildFileTypes = (list: string[], prefix: string) =>
  list.map((type) => `${prefix}${type}`);

const EXECUTABLE_FILE_TYPES = [
  'asp',
  'cgi',
  'cmd',
  'java-archive',
  'javascript',
  'jsp',
  'octet-stream',
  'php',
  'pkix-cert',
  'plain',
  'rust',
  'swift',
  'typescript',
  'vbscript',
  'vnd.android.package-archive',
  'vnd.macports.portpkg',
  'vnd.microsoft.portable-executable',
  'x-applescript',
  'x-aspx',
  'x-bat',
  'x-csh',
  'x-csh',
  'x-elf',
  'x-executable',
  'x-go',
  'x-groovy',
  'x-httpd-php',
  'x-javascript',
  'x-kotlin',
  'x-mach-binary',
  'x-msdos-program',
  'x-msdownload',
  'x-msi',
  'x-objcsrc',
  'x-perl',
  'x-powershell',
  'x-python',
  'x-rpm',
  'x-ruby',
  'x-scala',
  'x-sh',
  'x-shellscript',
  'x-vbe',
  'x-vbs',
  'x-wsh',
  'xml',
];

export const EXECUTABLE_FORMATS = [
  // Some executables report file.type = '' (empty string)
  '',
  ...buildFileTypes(EXECUTABLE_FILE_TYPES, 'application/'),
  ...buildFileTypes(EXECUTABLE_FILE_TYPES, 'text/'),
];

//
// Images
//
const GIF_FORMAT = 'image/gif';

export const IMAGE_FORMATS = ['image/jpeg', 'image/png', 'image/jpg'];
// image formats available for image content type
export const IMAGE_CONTENT_MIMES = [...IMAGE_FORMATS, GIF_FORMAT];
// image formats available for the organization's favicon
export const FAVICON_FORMATS = [
  ...IMAGE_CONTENT_MIMES,
  'image/vnd.microsoft.icon',
  'image/webp',
  'image/x-icon',
];

//
// Videos
//
// @note We also support video/webm (recorder), but it is not uploadable
export const VIDEO_FORMATS: Format[] = [
  {
    label: 'MP4',
    name: 'mp4',
    mimes: ['video/mp4'],
  },
];

const reduceNames = (format: Format) => format.name;
export const AUDIO_NAMES = AUDIO_FORMATS.flatMap(reduceNames);
export const WORD_NAMES = WORD_FORMATS.flatMap(reduceNames);
export const PDF_NAMES = PDF_FORMATS.flatMap(reduceNames);
export const DOCUMENT_NAMES = DOCUMENT_FORMATS.flatMap(reduceNames);
export const EXCEL_NAMES = EXCEL_FORMATS.flatMap(reduceNames);
export const POWER_POINT_NAMES = POWER_POINT_FORMATS.flatMap(reduceNames);
export const IMAGE_NAMES = ['jpeg', 'png', 'gif'];
export const VIDEO_NAMES = VIDEO_FORMATS.flatMap(reduceNames);

const reduceMimes = (format: Format) => format.mimes;
export const AUDIO_MIMES = AUDIO_FORMATS.flatMap(reduceMimes);
export const EXECUTABLE_MIMES = EXECUTABLE_FORMATS;
export const WORD_MIMES = WORD_FORMATS.flatMap(reduceMimes);
export const DOCUMENT_MIMES = DOCUMENT_FORMATS.flatMap(reduceMimes);
export const POWER_POINT_MIMES = POWER_POINT_FORMATS.flatMap(reduceMimes);
export const IMAGE_MIMES = IMAGE_CONTENT_MIMES;
export const VIDEO_MIMES = VIDEO_FORMATS.flatMap(reduceMimes);
